import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import { authService } from './auth-service';

/**
 * Auth state shape returned by useAuthState hooks.
 */
export type AuthState = {
    isAuthenticated: boolean;
    isLoading: boolean;
    account: AccountInfo | null;
    /** Stable identifier for the account (use this as dependency, not the account object) */
    accountId: string | null;
    logout: () => Promise<void>;
};

/**
 * Mock auth state for when authentication is disabled.
 * Use this directly instead of a hook when isAuthDisabled is true.
 */
export const DISABLED_AUTH_STATE: AuthState = {
    isAuthenticated: true,
    isLoading: false,
    account: null,
    accountId: null,
    logout: async () => {
        console.log('[Portal] Logout called but auth is disabled');
    }
};

/**
 * Hook to get auth state from MSAL.
 *
 * IMPORTANT: Only call this hook when auth is ENABLED.
 * When auth is disabled, use DISABLED_AUTH_STATE directly.
 *
 * This design avoids conditional hook calls which violate Rules of Hooks.
 */
export function useMsalAuthState(): AuthState {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const account = accounts[0] ?? null;

    // Subscribe to authService so we re-render when requiresInteraction changes
    const [requiresInteraction, setRequiresInteraction] = useState(authService.requiresInteraction);
    useEffect(() => {
        return authService.onChange(() => {
            setRequiresInteraction(authService.requiresInteraction);
        });
    }, []);

    return {
        isAuthenticated: isAuthenticated && !requiresInteraction,
        isLoading: inProgress !== 'none',
        account,
        accountId: account?.homeAccountId ?? null,
        logout: async () => {
            await instance.logoutRedirect();
        }
    };
}
